import React, { useEffect, useState } from 'react';
import {
	Button,
	Tab,
	Tabs,
	Typography
} from '@mui/material';
import {
	getConfigDetails,
	getAlertKpiProgramsSectors,
	saveAlertConfig,
	updateAlertConfig,
	getRuleTypeOptions
} from 'service/service';
import Loader from 'components/Loader/loader';
import PageHeader from 'components/Header/Page';
import AlertIcon from '../../assets/PepWise2.0/Alerts.svg';
import { useParams } from 'react-router-dom';
import SuccessConfirmationDialog from './SuccessModal';
import './alert-kpi.scss';
import GroupingAlertTab from './GroupingAlertTab';
import { SilencesTab } from './SilencesTab';
import { VariablesTab } from './VariablesTab';
import { ChannelsTab } from './ChannelsTab';
import { SourceTab } from './SourceTab';
import ErrorDialog from './ErrorModal';
import PageWrapper from 'components/Common/PageWraper';
import RetentionRuleTab from './RetentionRuleTab';
import AutoResolutionRuleTab from './AutoResolutionRuleTab';
import { useTheme } from "@mui/material/styles";

export interface VariableRow {
	isEnabled: boolean;
	type: string;
	name: string;
	value: string;
	identifier: string;
	uuid: string;
}

export interface SilenceRow {
	isEnabled: boolean;
	name: string;
	startTime : string;
	endTime: string;
	silencedChannels : Array<string>;
	message: string;
	uuid: string;
}

export interface ChannelRow {
	emailRecipients: any;
	emailSubject: string;
	htmlTemplate: any;
	payload: any;
	title: string;
	bodyTemplate: string;
	webhookUrl: string;
	teamsMail: string;
	templateName: string;
	status: string;
	channel: string;
}

const CreateAlertFramework = () => {
	const theme = useTheme();
	const colors = theme.palette;
	const [activeTab, setActiveTab] = useState(0);
	const [loading, setLoading] = useState(false);
	const [channels, setChannels] = useState<any>({});
	const { id: configId } = useParams<{ id: string }>();
	const [alertId, setAlertId] = useState(configId || null);
	const [variables, setVariables] = useState<VariableRow[]>([]);
	const [maintenanceWindows, setMaintenanceWindows] = useState<any>([])
	const [groupingAlertsConfig, setGroupingAlertsConfig] = useState({})
	const [openDialogSuccess, setOpenDialogSuccess] = useState(false)
	const [openErrorDialog, setOpenErrorDialog] = useState(false)
	const [errorMessage, setErrorMessage] = useState('')
	const [programOptions, setProgramOptions] = useState([{ title: '', value: '' }]);
	const [sectorOptions, setSectorOptions] = useState([{ title: '', value: '' }]);
	const [pipelines, setPipelines] = useState<any>([])
	const [alertTypeOptions, setAlertTypeOptions] = useState<any>([]);

	const [formData, setFormData] = useState<any>({
		program: '',
		project: '',
		sector: '',
		ruleName: '',
		alertType: '',
		market: '',
		sourceConfig: { sourceType: '', query: '' },
		emailConfig: { isEnabled: false, emailSubject: '', htmlTemplate: '', emailRecipients: [] },
		serviceNowConfig: { isEnabled: false, payload: {}, serviceNowUrl: '' },
		teamsConfig: { isEnabled: false, title: '', bodyTemplate: '', webhookUrl: '', teamsMail: '' },
		retentionRuleConfig: { retentionDuration: null, retainResolvedAlerts: false, alertType: '' },
		autoResolutionRuleConfig: { isEnabled: false, queryVariableName: '', queryVariableValue: '', emailBody: '' },
		isCreate: false
	});

	const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
		setActiveTab(newValue);
	};

	const handleSaveNext = async () => {
		if (!isFormValid()) {
			alert("Please fill in all mandatory fields.");
			return;
		}

		const response = await saveConfig();

		if (response.statusCode !== 200) {
			setErrorMessage(response.data.message);
			setOpenErrorDialog(true);
			return;
		}

		if (activeTab === 4 && !configId) {
			setOpenDialogSuccess(true);
			return;
		}

		if (activeTab === 6) {
			setOpenDialogSuccess(true);
			return;
		}

		setActiveTab(activeTab+1);
	};

	const handleCancel = async () => {
		window.location.href = '/alertFramework/alert';
	};


	const applyChannelConfig = (
			configData: any,
			channels: any,
			channelKey: string,
			targetKey: string
		) => {
			if (channels[channelKey]) {
				configData[targetKey] = channels[channelKey];
			} else if (configData[targetKey]) {
				configData[targetKey] = {};
			}
		};

		const shouldIncludeRetentionConfig = (config: any) =>
			config?.retentionDuration ||
			config?.retainResolvedAlerts !== false ||
			config?.alertType;

		const shouldIncludeAutoResolutionConfig = (config: any) =>
			config?.isEnabled ||
			config?.queryVariableName ||
			config?.queryVariableValue ||
			config?.emailBody;

	const saveConfig = async () => {
		setLoading(true);

		try {
			if (alertId) {
			const configData = { ...formData };

			applyChannelConfig(configData, channels, 'Email', 'emailConfig');
			applyChannelConfig(configData, channels, 'Service Now', 'serviceNowConfig');
			applyChannelConfig(configData, channels, 'Teams', 'teamsConfig');

			if (!configId) {
				configData.isCreate = true;
				// During the create wizard, avoid sending retentionRuleConfig in the PUT payload.
				// This prevents retention validation from blocking the create flow.
				delete configData.retentionRuleConfig;
			}

			configData.variables = variables;
			configData.maintenanceWindows = maintenanceWindows;
			configData.groupingAlertsConfig = groupingAlertsConfig;

			if (pipelines?.length) {
				configData.pipelines = pipelines;
			}

			return await updateAlertConfig(alertId, configData);
			}

			const requestBody: any = {
			program: formData.program,
			project: formData.project,
			sector: formData.sector,
			ruleName: formData.ruleName,
			alertType: formData.alertType,
			sourceConfig: formData.sourceConfig,
			};

			if (shouldIncludeRetentionConfig(formData.retentionRuleConfig)) {
			requestBody.retentionRuleConfig = formData.retentionRuleConfig;
			}

			if (shouldIncludeAutoResolutionConfig(formData.autoResolutionRuleConfig)) {
			requestBody.autoResolutionRuleConfig =
				formData.autoResolutionRuleConfig;
			}

			const response = await saveAlertConfig(requestBody);

			if (response.data?._id) {
			setAlertId(response.data._id);
			}

			return response;
		} finally {
			setLoading(false);
		}
	};

	const isFormValid = () => {
		const mandatoryFieldsValid = 
			formData.program &&
			formData.project &&
			formData.sector &&
			formData.ruleName &&
			formData.alertType &&
			formData.sourceConfig?.sourceType &&
			formData.sourceConfig?.query &&
			formData.sourceConfig?.dbName &&
			formData.sourceConfig?.collectionName;
	
		return mandatoryFieldsValid;
	};

	const fetchData = async () => {
		setLoading(true);
		const result: any = await getAlertKpiProgramsSectors();
        if(result.data){
            setProgramOptions(result.data.programs)
            setSectorOptions(result.data.sectors)
        }
		setLoading(false);
	};

	const buildChannelsFromAlertConfig = (alertConfig: any) => {
		const channels: any = {};

		if (alertConfig.emailConfig?.emailSubject) {
			channels['Email'] = alertConfig.emailConfig;
		}

		if (alertConfig.serviceNowConfig?.payload) {
			channels['Service Now'] = {
			...alertConfig.serviceNowConfig,
			payload: JSON.stringify(alertConfig.serviceNowConfig.payload),
			};
		}

		if (
			alertConfig.teamsConfig?.webhookUrl ||
			alertConfig.teamsConfig?.teamsMail
		) {
			const teamsChannel = { ...alertConfig.teamsConfig };

			if (alertConfig.teamsMail?.email) {
			teamsChannel.teamsMail = alertConfig.teamsMail.email;
			}

			if (!teamsChannel.type && !teamsChannel.channel) {
				teamsChannel.channel = 'Teams';
			}

			channels['Teams'] = teamsChannel;
		}

		return channels;
		};

		const mapAlertConfigToFormData = (alertConfig: any) => ({
		program: alertConfig.program,
		project: alertConfig.project,
		sector: alertConfig.sector,
		ruleName: alertConfig.ruleName,
		alertType: alertConfig.alertType,
		sourceConfig: alertConfig.sourceConfig,
		emailConfig: alertConfig.emailConfig,
		serviceNowConfig: alertConfig.serviceNowConfig,
		teamsConfig: alertConfig.teamsConfig,
		retentionRuleConfig: alertConfig.retentionRuleConfig,
		autoResolutionRuleConfig: alertConfig.autoResolutionRuleConfig,
		isCreate: false,
		});

	const fetchConfig = async () => {
		setLoading(true);

		const response: any = await getConfigDetails(configId, {});
		const alertConfig = response.data?.alertConfig;

		if (!alertConfig) {
			setLoading(false);
			return;
		}

		setFormData(mapAlertConfigToFormData(alertConfig));
		setMaintenanceWindows(alertConfig.maintenanceWindows);
		setVariables(alertConfig.variables);
		setGroupingAlertsConfig(alertConfig.groupingAlertsConfig);

		if (alertConfig.pipelines) {
			setPipelines(alertConfig.pipelines);
		}

		setChannels(buildChannelsFromAlertConfig(alertConfig));
		setLoading(false);
	};
	
	const fetchRetentionRuleOptions = async () => {
		const response = await getRuleTypeOptions();
		if(response.data){
			setAlertTypeOptions(response.data.map((item: string) => ({ label: item, value: item })));
		}
	}

	useEffect(() => {
		setLoading(true);
		// Fetch and set initial retention rule values
		if(activeTab === 5) {
			fetchRetentionRuleOptions();
		}
		setLoading(false);
	}, [activeTab]);

	useEffect(() => {
		fetchData();
		if(configId){
			fetchConfig();
		}
	}, []);

	return loading ? (
		<Loader />
	) : (
		<PageWrapper>
			<PageHeader title={[{text: "Alerts", link: '/alertFramework/alert'}, 
				{text: configId ? `Edit Alert: ${configId}` : 'Create Alert'}]} 
				icon={AlertIcon} alertContent={'All with * are mandatory'}>
				<Typography
					sx={{
						color: `${colors.buttons.primary.color} !important`,
						cursor: 'pointer',
						marginTop: '6px',
						marginRight: '25px'
					}}
					onClick={handleCancel}
				>
					CANCEL
				</Typography>
				<Button variant="pepwiseSecondary" onClick={handleSaveNext}  disabled={!isFormValid()} >
				{activeTab === 6 ? "SAVE" : "SAVE & NEXT"}
				</Button>
				<SuccessConfirmationDialog open={openDialogSuccess} onClose={() => {setOpenDialogSuccess(false); handleCancel()}} />
				<ErrorDialog open={openErrorDialog} onClose={() => {setOpenErrorDialog(false); setErrorMessage('')}} errorMessage={errorMessage}/>
			</PageHeader>

			<Tabs
				value={activeTab}
				onChange={handleTabChange}
				variant="fullWidth"
				sx={{
					px: {
						xs: 1,
						sm: 2,
						md: 2
					},
					mt: 2,
					width: '100%',
					boxSizing: 'border-box',

					'& .MuiTabs-scroller': {
						overflow: 'visible !important'
					},

					'& .MuiTabs-flexContainer': {
						width: '100%',
						flexWrap: {
							xs: 'wrap',
							md: 'nowrap'
						},
						gap: {
							xs: '6px',
							sm: '8px',
							md: 0
						}
					},

					'& .MuiTabs-indicator': {
						display: 'none'
					},

					'& .MuiTab-root': {
						...theme.typography.tabLabel,
						color: `${theme.palette.Tab.inActive.newColor} !important`,
						textTransform: 'none',
						fontSize: {
							xs: '0.75rem',
							sm: '0.8rem',
							md: '0.85rem',
							lg: '0.95rem'
						},
						minHeight: '46px',
						minWidth: '0 !important',
						maxWidth: 'none !important',
						width: 'auto !important',
						flex: {
							xs: '1 1 calc(50% - 6px)',
							sm: '1 1 calc(33.333% - 8px)',
							md: '1 1 0'
						},
						padding: {
							xs: '6px 6px',
							sm: '8px 8px',
							md: '8px 10px',
							lg: '8px 16px'
						},
						lineHeight: 1.2,
						whiteSpace: 'normal',
						textAlign: 'center',
						backgroundColor: `${theme.palette.Tab.inActive.bgColor} !important`,
						borderBottom: '4px solid transparent !important',
						boxSizing: 'border-box',
						opacity: 1,
						top: '0 !important',
					},

					'& .MuiTab-root.Mui-selected, & .MuiTab-root.Mui-selected:hover': {
						color: `${theme.palette.Tab.Active.newColor} !important`,
						backgroundColor: `${theme.palette.Tab.Active.newBgColor} !important`,
						borderBottomColor: `${theme.palette.Tab.Active.newBorderColor} !important`,
						borderRadius: 0,
					},

					'& .MuiTab-root.Mui-disabled': {
						color: `${theme.palette.Tab.inActive.color} !important`,
						backgroundColor: `${theme.palette.Tab.inActive.bgColor} !important`,
						opacity: 0.5
					}
				}}
			>
				<Tab label="Alert Source*" />
				<Tab label="Delivery Channels*" />
				<Tab label="Variables/Labels" />
				<Tab label="Silences" />
				<Tab label="Grouped Alerts" />
				<Tab label="Retention Rule" />
				<Tab label="Auto- Resolution Rule" />
			</Tabs>

			{/* Form Section */}
			{activeTab === 0 && (
				<SourceTab isEdit={!!configId} sourceData={formData} setSourceData={setFormData} programOptions={programOptions} sectorOptions={sectorOptions} pipelines={pipelines} setPipelines={setPipelines}/>
			)}
			{activeTab === 1 && (
				<ChannelsTab channels={channels} setChannels={setChannels}/>
			)}
			{activeTab === 2 && (
				<VariablesTab variables={variables} setVariables={setVariables}/>
			)}
			{activeTab === 3 && (
				<SilencesTab maintenanceWindows={maintenanceWindows} setMaintenanceWindows={setMaintenanceWindows}/>
			)}
			{activeTab === 4 && (
				<GroupingAlertTab groupingAlertsConfig={groupingAlertsConfig} setGroupingAlertsConfig={setGroupingAlertsConfig}></GroupingAlertTab>
			)}
			{activeTab === 5 && (
				<RetentionRuleTab formData={formData} setFormData={setFormData} alertTypeOptions = {alertTypeOptions}/>
			)}
			{activeTab === 6 && (
				<AutoResolutionRuleTab formData={formData} setFormData={setFormData}/>
			)}
		</PageWrapper>
	);
};

export default CreateAlertFramework;
